import React, { useState } from 'react';
import { 
  Navbar, Nav, NavDropdown, Button, Badge, Modal, Alert, Spinner, 
  Form, InputGroup, Card
} from 'react-bootstrap';
import { 
  FaBars, FaSignOutAlt, FaCog, FaLock, FaSave, FaTimes, FaEye, FaEyeSlash,
  FaFlask // Ícone para indicar ambiente de teste
} from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import supabaseAdmin from '../../supabaseAdmin';

// Função auxiliar para gerar senha temporária
const generateTemporaryPassword = () => {
  const length = 12;
  const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%";
  let password = "";
  for (let i = 0; i < length; i++) {
    password += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return password;
};

const Header = ({ toggleSidebar }) => {
  const navigate = useNavigate();
  const { 
    user, 
    userProfile, 
    userRoles, 
    logout
  } = useAuth();
  
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [error, setError] = useState(null);
  const [savingPassword, setSavingPassword] = useState(false);
  
  // Estados para alteração de senha própria
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    new: false,
    confirm: false
  });

  // Verificar se está em ambiente de desenvolvimento
  const isDevelopment = process.env.REACT_APP_ENV === 'development';

  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      setError(null);
      
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
      setError('Erro ao fazer logout. Tente novamente.');
    } finally {
      setLoggingOut(false);
      setShowLogoutModal(false);
    }
  };

  // Função para gerar senha automática
  const handleGeneratePassword = () => {
    const newPassword = generateTemporaryPassword();
    setPasswordForm({
      ...passwordForm,
      newPassword: newPassword,
      confirmPassword: newPassword
    });
  };

  // Função para copiar senha para clipboard
  const handleCopyPassword = async (password) => {
    try {
      await navigator.clipboard.writeText(password);
      alert('✅ Senha copiada para a área de transferência!');
    } catch (err) {
      // Fallback para navegadores mais antigos
      const textArea = document.createElement('textarea');
      textArea.value = password;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('✅ Senha copiada para a área de transferência!');
    }
  };

  const handlePasswordChange = async () => {
    try {
      setSavingPassword(true);
      setError(null);
      
      // Validações
      if (passwordForm.newPassword !== passwordForm.confirmPassword) {
        setError('As senhas não coincidem');
        return;
      }
      
      if (passwordForm.newPassword.length < 8) {
        setError('A nova senha deve ter pelo menos 8 caracteres');
        return;
      }
      
      console.log('🔐 Alterando senha do usuário:', user.id);

      // Alterar senha usando o cliente admin
      const { error: updateError } = await supabaseAdmin.auth.admin.updateUserById(
        user.id,
        {
          password: passwordForm.newPassword
        }
      );

      if (updateError) {
        console.error('❌ Erro ao alterar senha:', updateError);
        throw new Error(`Erro ao alterar senha: ${updateError.message}`);
      }

      console.log('✅ Senha alterada com sucesso');
      
      // Limpar formulário
      setPasswordForm({
        newPassword: '',
        confirmPassword: ''
      });
      
      alert('Senha alterada com sucesso! Você será redirecionado para fazer login novamente.');
      
      // Fechar modal
      setShowSettingsModal(false);
      
      // Fazer logout automaticamente após alteração da senha
      await logout();
      navigate('/login');
      
    } catch (error) {
      console.error('💥 Erro ao alterar senha:', error);
      setError(`Erro ao alterar senha: ${error.message || 'Desconhecido'}`);
    } finally {
      setSavingPassword(false);
    }
  };

  const resetModal = () => {
    setPasswordForm({
      newPassword: '',
      confirmPassword: ''
    });
    setError(null);
    setShowPasswords({
      new: false,
      confirm: false
    });
  };

  const formatUserName = () => {
    if (userProfile?.full_name) {
      const names = userProfile.full_name.split(' ');
      if (names.length >= 2) {
        return `${names[0]} ${names[names.length - 1]}`;
      }
      return names[0];
    }
    return user?.email?.split('@')[0] || 'Usuário';
  };

  const getRolesBadges = () => {
    if (!userRoles || userRoles.length === 0) {
      return <Badge bg="warning" text="dark">Sem roles</Badge>;
    }

    return userRoles.map(role => (
      <Badge 
        key={role.id} 
        bg={role.name === 'admin' ? 'danger' : 'secondary'} 
        className="me-1"
      >
        {role.name}
      </Badge>
    ));
  };

  return (
    <>
      <Navbar bg="white" className="border-bottom px-3 admin-header">
        <div className="d-flex align-items-center">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={toggleSidebar}
            className="me-3"
          >
            <FaBars />
          </Button>
          
          <div className="d-flex align-items-center">
            <Navbar.Brand className="mb-0 fw-bold">
              Portal de Expositores
            </Navbar.Brand>
            
            {/* Indicador de Ambiente de Desenvolvimento */}
            {isDevelopment && (
              <Badge 
                bg="warning" 
                text="dark" 
                className="ms-3 px-4 py-3 development-badge"
                style={{
                  fontSize: '0.95rem',
                  fontWeight: '700',
                  letterSpacing: '1px',
                  borderRadius: '25px',
                  boxShadow: '0 4px 8px rgba(255, 193, 7, 0.4)',
                  border: '3px solid #ffc107',
                  animation: 'pulse 2s infinite',
                  textShadow: '1px 1px 2px rgba(0, 0, 0, 0.2)'
                }}
              >
                <FaFlask className="me-2" style={{ fontSize: '0.9rem' }} />
                AMBIENTE DE TESTE
              </Badge>
            )}
          </div>
        </div>

        <Nav className="ms-auto align-items-center">
          {/* Informações do usuário */}
          <NavDropdown
            title={
              <span className="d-flex align-items-center">
                <div className="avatar-container">
                  <span className="avatar-placeholder">
                    {userProfile?.full_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                  </span>
                </div>
                <span className="ms-2 d-none d-md-inline user-name">
                  {formatUserName()}
                </span>
              </span>
            }
            id="user-dropdown"
            align="end"
            className="user-dropdown-toggle"
          >
            {/* Informações do perfil */}
            <div className="dropdown-header">
              <div className="fw-bold">{userProfile?.full_name || 'Nome não informado'}</div>
              <small className="text-muted">{user?.email}</small>
              <div className="mt-2">
                {getRolesBadges()}
              </div>
            </div>
            
            <NavDropdown.Divider />
            
            {/* Nova opção de Configurações */}
            <NavDropdown.Item 
              onClick={() => {
                resetModal();
                setShowSettingsModal(true);
              }}
              className="text-primary"
            >
              <FaCog className="me-2" />
              Configurações
            </NavDropdown.Item>
            
            <NavDropdown.Divider />
            
            <NavDropdown.Item 
              onClick={() => setShowLogoutModal(true)}
              className="text-danger"
              disabled={loggingOut}
            >
              <FaSignOutAlt className="me-2" />
              Sair
            </NavDropdown.Item>
          </NavDropdown>
        </Nav>
      </Navbar>

      {/* Modal de Configurações */}
      <Modal 
        show={showSettingsModal} 
        onHide={() => setShowSettingsModal(false)}
        size="lg"
        centered
        className="settings-modal"
      >
        <Modal.Header className="border-0 pb-0">
          <div className="w-100 d-flex justify-content-between align-items-center">
            <div>
              <Modal.Title className="fw-bold fs-4 mb-1">Configurações</Modal.Title>
              <small className="text-muted">Gerencie suas configurações de conta</small>
            </div>
            <Button 
              variant="link" 
              className="text-muted p-0 border-0"
              onClick={() => setShowSettingsModal(false)}
            >
              <FaTimes size={20} />
            </Button>
          </div>
        </Modal.Header>
        
        <Modal.Body className="pt-0">
          {error && (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          )}

          {/* Seção de Alteração de Senha */}
          <Card className="border-0 bg-light mt-3">
            <Card.Body className="p-4">
              <div className="d-flex align-items-center mb-4">
                <div className="settings-icon-wrapper me-3">
                  <FaLock className="text-primary" />
                </div>
                <div>
                  <h6 className="mb-0 fw-semibold">Alterar Senha</h6>
                  <small className="text-muted">Defina uma nova senha para sua conta</small>
                </div>
              </div>
              
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Nova Senha</Form.Label>
                  <div className="d-flex gap-2 mb-2">
                    <Button
                      variant="outline-info"
                      size="sm"
                      onClick={handleGeneratePassword}
                      disabled={savingPassword}
                    >
                      <FaLock className="me-1" />
                      Gerar Senha
                    </Button>
                    {passwordForm.newPassword && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => handleCopyPassword(passwordForm.newPassword)}
                        disabled={savingPassword}
                      >
                        📋 Copiar
                      </Button>
                    )}
                  </div>
                  <InputGroup>
                    <Form.Control
                      type={showPasswords.new ? "text" : "password"}
                      placeholder="Digite sua nova senha"
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                      disabled={savingPassword}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}
                    >
                      {showPasswords.new ? <FaEyeSlash /> : <FaEye />}
                    </Button>
                  </InputGroup>
                  <Form.Text className="text-muted">
                    A senha deve ter pelo menos 8 caracteres
                  </Form.Text>
                </Form.Group>
                
                <Form.Group className="mb-4">
                  <Form.Label className="fw-medium">Confirmar Nova Senha</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type={showPasswords.confirm ? "text" : "password"}
                      placeholder="Confirme sua nova senha"
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                      disabled={savingPassword}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}
                    >
                      {showPasswords.confirm ? <FaEyeSlash /> : <FaEye />}
                    </Button>
                  </InputGroup>
                  {passwordForm.newPassword && passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword && (
                    <Form.Text className="text-danger">
                      As senhas não coincidem
                    </Form.Text>
                  )}
                </Form.Group>
                
                {passwordForm.newPassword && passwordForm.confirmPassword && passwordForm.newPassword === passwordForm.confirmPassword && (
                  <div className="alert alert-warning" style={{ fontSize: '0.9rem', padding: '0.75rem' }}>
                    <strong>⚠️ Importante:</strong> Após alterar a senha, você será automaticamente desconectado e redirecionado para a tela de login para entrar com a nova senha.
                  </div>
                )}
                
                <Button 
                  variant="primary" 
                  onClick={handlePasswordChange}
                  disabled={savingPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
                  className="w-100"
                  size="lg"
                >
                  {savingPassword ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Alterando senha...
                    </>
                  ) : (
                    <>
                      <FaSave className="me-2" />
                      Alterar Senha e Sair
                    </>
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Modal.Body>
        
        <Modal.Footer className="border-0 pt-0">
          <Button 
            variant="outline-secondary" 
            onClick={() => setShowSettingsModal(false)}
            disabled={savingPassword}
            className="px-4"
          >
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de confirmação de logout */}
      <Modal 
        show={showLogoutModal} 
        onHide={() => setShowLogoutModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Logout</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}
          
          <p>Tem certeza que deseja sair do sistema?</p>
          
          {userRoles && userRoles.length > 0 && (
            <div className="mt-3">
              <small className="text-muted">Você está logado com os seguintes roles:</small>
              <div className="mt-1">
                {getRolesBadges()}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowLogoutModal(false)}
            disabled={loggingOut}
          >
            Cancelar
          </Button>
          <Button 
            variant="danger" 
            onClick={handleLogout}
            disabled={loggingOut}
          >
            {loggingOut ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saindo...
              </>
            ) : (
              <>
                <FaSignOutAlt className="me-2" />
                Sair
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <style jsx>{`
        .settings-modal .modal-content {
          border: none;
          border-radius: 16px;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        }
        
        .settings-icon-wrapper {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: rgba(var(--bs-primary-rgb), 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .card {
          transition: all 0.2s ease;
          border-radius: 12px;
        }
        
        .card:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
        }
        
        .input-group .btn {
          border-left: none;
        }
        
        .form-control:focus + .btn,
        .form-control:focus {
          border-color: var(--bs-primary);
          box-shadow: 0 0 0 0.2rem rgba(var(--bs-primary-rgb), 0.25);
        }

        .development-badge {
          position: relative;
          text-transform: uppercase;
          white-space: nowrap;
          min-width: 180px;
          text-align: center;
        }

        /* Animação de pulso para chamar atenção */
        @keyframes pulse {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.08);
            opacity: 0.85;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        /* Responsividade - ajustar tamanho em telas menores */
        @media (max-width: 992px) {
          .development-badge {
            font-size: 0.85rem !important;
            padding: 8px 16px !important;
            min-width: 160px;
          }
          
          .development-badge .fa-flask {
            font-size: 0.8rem !important;
          }
        }

        @media (max-width: 768px) {
          .development-badge {
            font-size: 0.75rem !important;
            padding: 6px 12px !important;
            min-width: 140px;
            letter-spacing: 0.5px !important;
          }
          
          .development-badge .fa-flask {
            font-size: 0.7rem !important;
            margin-right: 4px !important;
          }
        }

        @media (max-width: 576px) {
          .development-badge {
            font-size: 0.65rem !important;
            padding: 4px 8px !important;
            min-width: 120px;
            letter-spacing: 0.3px !important;
          }
          
          .development-badge .fa-flask {
            font-size: 0.6rem !important;
          }
        }
      `}</style>
    </>
  );
};

export default Header;